import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { format, isWithinInterval, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { useTranslation } from '@ibk/shared-translation';
import { Calendar, Loading } from '@ibk/shared-ui';
import { formatCurrency } from '@ibk/shared-utils';
import { useLazyGetAccountLimitsQuery } from '../../services/limitsApi';
import { AccountTransactionalLimitValueStatus, LimitPeriod, LimitTarget } from '../../types/limits';
import styles from './LimitsHistory.module.css';
export function LimitsHistory() {
    const { t } = useTranslation();
    const [getAccountLimits, { data: limitsData, isLoading, isError }] = useLazyGetAccountLimitsQuery();
    const [filterStatus, setFilterStatus] = useState('all');
    const [dateRange, setDateRange] = useState({
        start: undefined,
        end: undefined,
    });
    const [showCalendar, setShowCalendar] = useState(false);
    useEffect(() => {
        const accountInfo = getSelectedAccountInfo();
        if (accountInfo?.accountId) {
            getAccountLimits({
                transactionType: 'PIX',
                all: true,
            });
        }
    }, [getAccountLimits]);
    const getStatusBadge = (status) => {
        switch (status) {
            case AccountTransactionalLimitValueStatus.REALIZED:
                return {
                    text: t('limits.filter-approved'),
                    className: styles.approved,
                };
            case AccountTransactionalLimitValueStatus.PENDING:
                return {
                    text: t('limits.filter-pending'),
                    className: styles.pending,
                };
            case AccountTransactionalLimitValueStatus.FAILED:
                return {
                    text: t('limits.filter-rejected'),
                    className: styles.rejected,
                };
            default:
                return {
                    text: '',
                    className: '',
                };
        }
    };
    const formatDate = (date) => {
        return format(new Date(date), 'dd/MM/yyyy', { locale: ptBR });
    };
    const getLimitDescription = (limit) => {
        const targetLabels = {
            [LimitTarget.PERSON]: t('limits.section-person'),
            [LimitTarget.ORGANIZATION]: t('limits.section-business'),
            [LimitTarget.ALL]: t('limits.section-scheduled'),
        };
        const periodLabels = {
            [LimitPeriod.DAILY]: t('limits.day'),
            [LimitPeriod.NIGHTLY]: t('limits.night'),
            [LimitPeriod.ALL]: '',
        };
        const targetLabel = targetLabels[limit.limitTarget] || '';
        const periodLabel = periodLabels[limit.limitPeriod] || '';
        if (limit.limitTarget === LimitTarget.ALL) {
            return targetLabel;
        }
        return periodLabel ? `${targetLabel} - ${periodLabel}` : targetLabel;
    };
    const filteredLimits = limitsData?.result?.filter((limit) => {
        if (filterStatus !== 'all') {
            if (filterStatus === 'approved' && limit.status !== AccountTransactionalLimitValueStatus.REALIZED)
                return false;
            if (filterStatus === 'pending' && limit.status !== AccountTransactionalLimitValueStatus.PENDING)
                return false;
            if (filterStatus === 'rejected' && limit.status !== AccountTransactionalLimitValueStatus.FAILED)
                return false;
        }
        if (dateRange.start && dateRange.end) {
            const limitDate = parseISO(limit.createdAt.toString());
            const startDate = parseISO(dateRange.start);
            const endDate = parseISO(dateRange.end);
            endDate.setHours(23, 59, 59, 999);
            if (!isWithinInterval(limitDate, { start: startDate, end: endDate })) {
                return false;
            }
        }
        return true;
    }) || [];
    const handleDateRangeSelect = (value) => {
        const period = value;
        setDateRange({ start: period?.start, end: period?.end });
    };
    const clearDateFilter = () => {
        setDateRange({ start: undefined, end: undefined });
    };
    const handleRetry = () => {
        const accountInfo = getSelectedAccountInfo();
        if (accountInfo?.accountId) {
            getAccountLimits({
                transactionType: 'PIX',
                all: true,
            });
        }
    };
    if (isLoading) {
        return (_jsx("div", { className: styles.loading, children: _jsx(Loading, {}) }));
    }
    if (isError) {
        return (_jsxs("div", { className: styles.error, children: [_jsx("svg", { className: styles.errorIcon, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', children: _jsx("path", { strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 2, d: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' }) }), _jsx("h3", { className: styles.errorTitle, children: t('limits.error-title') }), _jsx("p", { className: styles.errorDescription, children: t('limits.error-description') }), _jsx("button", { className: styles.retryButton, onClick: handleRetry, children: t('limits.retry') })] }));
    }
    const hasLimits = limitsData?.result && limitsData.result.length > 0;
    if (!hasLimits) {
        return (_jsxs("div", { className: styles.empty, children: [_jsx("svg", { className: styles.emptyIcon, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', children: _jsx("path", { strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 2, d: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' }) }), _jsx("h3", { className: styles.emptyTitle, children: t('limits.history-empty-title') }), _jsx("p", { className: styles.emptyDescription, children: t('limits.history-empty-description') })] }));
    }
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.filters, children: [_jsx("button", { className: `${styles.filterChip} ${filterStatus === 'all' ? styles.active : ''}`, onClick: () => setFilterStatus('all'), "data-testid": 'filter-all', children: t('limits.filter-all') }), _jsx("button", { className: `${styles.filterChip} ${filterStatus === 'approved' ? styles.active : ''}`, onClick: () => setFilterStatus('approved'), "data-testid": 'filter-approved', children: t('limits.filter-approved') }), _jsx("button", { className: `${styles.filterChip} ${filterStatus === 'pending' ? styles.active : ''}`, onClick: () => setFilterStatus('pending'), "data-testid": 'filter-pending', children: t('limits.filter-pending') }), _jsx("button", { className: `${styles.filterChip} ${filterStatus === 'rejected' ? styles.active : ''}`, onClick: () => setFilterStatus('rejected'), "data-testid": 'filter-rejected', children: t('limits.filter-rejected') }), _jsxs("div", { className: styles.periodFilterWrapper, children: [_jsxs("button", { className: `${styles.periodButton} ${dateRange.start && dateRange.end ? styles.active : ''}`, onClick: () => setShowCalendar(!showCalendar), "data-testid": 'filter-period', children: [_jsx("svg", { className: styles.calendarIcon, viewBox: '0 0 20 20', fill: 'currentColor', children: _jsx("path", { fillRule: 'evenodd', d: 'M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z', clipRule: 'evenodd' }) }), t('limits.filter-period')] }), showCalendar && (_jsx("div", { className: styles.periodCalendarWrapper, children: _jsx(Calendar, { dataTestId: 'period-calendar', selectionType: 'period', initialDate: { start: dateRange.start, end: dateRange.end }, select: handleDateRangeSelect, size: 'small', open: showCalendar, close: () => setShowCalendar(false) }) })), dateRange.start && dateRange.end && (_jsx("button", { className: styles.clearFilter, onClick: clearDateFilter, "data-testid": 'clear-period-filter', children: "\u2715" }))] })] }), _jsx("div", { className: styles.list, children: filteredLimits.map((limit, index) => {
                    const badge = getStatusBadge(limit.status);
                    return (_jsxs("div", { className: styles.listItem, "data-testid": 'history-item', children: [_jsxs("div", { className: styles.listItemLeft, children: [_jsxs("div", { className: styles.listItemTop, children: [_jsx("span", { className: `${styles.badge} ${badge.className}`, children: badge.text }), _jsx("span", { className: styles.listItemDate, children: formatDate(limit.createdAt) })] }), _jsx("span", { className: styles.listItemDescription, children: getLimitDescription(limit) })] }), _jsx("div", { className: styles.listItemRight, children: _jsx("div", { className: styles.listItemValue, children: formatCurrency(limit.pendingLimitValue || limit.currentLimitValue) }) })] }, `${limit.transactionalRule}-${index}`));
                }) })] }));
}
